// What a new trigger does, in words, for its approval card. Schedule times
// are stored in UTC; the card reads them in the viewer's time zone too.

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const andList = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

// The next time a UTC schedule fires after ``now``: weekly days 1..7 are
// Monday..Sunday. Read on its real date, the viewer's clock is right for this
// season, and a time that crosses midnight lands on the right local day.
function nextUtc(
  now: Date,
  hhmm: string,
  isoDay?: number,
  monthDay?: number
): Date {
  const [h, m] = hhmm.split(":").map(Number);
  const at = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m)
  );
  if (monthDay !== undefined) {
    at.setUTCDate(monthDay);
    if (at <= now) at.setUTCMonth(at.getUTCMonth() + 1, monthDay);
    return at;
  }
  if (isoDay !== undefined) {
    const today = ((now.getUTCDay() + 6) % 7) + 1;
    at.setUTCDate(at.getUTCDate() + ((isoDay - today + 7) % 7));
  }
  if (at <= now)
    at.setUTCDate(at.getUTCDate() + (isoDay !== undefined ? 7 : 1));
  return at;
}

const clock = (d: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);

const weekday = (d: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "long" }).format(d);

const dayOfMonth = (d: Date, timeZone: string) =>
  Number(
    new Intl.DateTimeFormat("en-GB", { timeZone, day: "numeric" }).format(d)
  );

function dayList(days: string[]): string {
  if (days.length === 7) return "day";
  if (days.join() === DAY_NAMES.slice(0, 5).join()) return "weekday";
  return andList(days);
}

function inZones(utc: Date[], timeZone: string): string {
  const local = andList(utc.map((d) => clock(d, timeZone)));
  const inUtc = andList(utc.map((d) => clock(d, "UTC")));
  return timeZone === "UTC"
    ? `${inUtc} UTC`
    : `${local} your time (${inUtc} UTC)`;
}

/** "Every Monday at 09:00 your time (06:00 UTC)", or null when the
 * definition is not a schedule this card knows. */
export function describeSchedule(
  definition: Record<string, unknown>,
  timeZone: string,
  now: Date = new Date()
): string | null {
  if (definition.type !== "schedule_v2") return null;
  const times = strings(definition.times);
  switch (definition.action) {
    case "daily": {
      if (!times.length) return null;
      return `Every day at ${inZones(
        times.map((t) => nextUtc(now, t)),
        timeZone
      )}`;
    }
    case "weekly": {
      const days = strings(definition.days_of_week).map(Number);
      if (!days.length || !times.length) return null;
      const instants = days.flatMap((d) =>
        times.map((t) => nextUtc(now, t, d))
      );
      const localDays = [...new Set(instants.map((d) => weekday(d, timeZone)))];
      const ordered = DAY_NAMES.filter((n) => localDays.includes(n));
      // Each time at its next run: the viewer's clock can change before a
      // later day's run, so the first day listed is not the one to read.
      const soonest = (t: string) =>
        days.map((d) => nextUtc(now, t, d)).reduce((a, b) => (b < a ? b : a));
      const at = inZones(times.map(soonest), timeZone);
      return `Every ${dayList(ordered)} at ${at}`;
    }
    case "monthly": {
      const day = Number(definition.day_of_month);
      if (!day || !times.length) return null;
      const local = dayOfMonth(
        nextUtc(now, times[0], undefined, day),
        timeZone
      );
      return `Every month on day ${local} at ${inZones(
        times.map((t) => nextUtc(now, t, undefined, day)),
        timeZone
      )}`;
    }
    case "interval": {
      const value = Number(definition.interval_value);
      const unit = definition.interval_unit === "hours" ? "hour" : "minute";
      if (!value) return null;
      return `Every ${value === 1 ? unit : `${value} ${unit}s`}`;
    }
    case "cron_expression":
      return typeof definition.cron_expression === "string"
        ? `On the schedule ${definition.cron_expression} (UTC)`
        : null;
    default:
      return null;
  }
}

export interface TriggerInput {
  label: string;
  value: string;
  known: boolean;
}

/** Each mapped flow input by the label the editor shows, with what it gets:
 * a fixed value, or the event field it is fed from. */
export function describeInputs(
  definition: Record<string, unknown>,
  inputLabels: Map<string, string>
): TriggerInput[] {
  const map = isRecord(definition.input_map) ? definition.input_map : {};
  return Object.entries(map).map(([key, raw]) => {
    // Keyed by the input's node id, or by its label, which the server maps
    // to the same input.
    const nodeId = key.replace(/::input$/, "");
    const label =
      inputLabels.get(nodeId) ??
      ([...inputLabels.values()].includes(key) ? key : undefined);
    const fixed = isRecord(raw) && raw.__custom__ === true ? raw.value : raw;
    const value =
      isRecord(raw) && raw.__custom__ === true
        ? String(fixed)
        : typeof raw === "string" && definition.type !== "schedule_v2"
          ? `the event's ${raw}`
          : String(fixed);
    return { label: label ?? key, value, known: label !== undefined };
  });
}
